import React, { useCallback, useEffect, useState } from "react";
import {
  Search,
  Filter,
  FileText,
  AlertCircle,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
} from "lucide-react";
import AdvocateObjectForm from "./AdvocateObjectForm";
import { useAuth } from "../../../context/AuthProvider";
// import useSWR from "swr";
import { deleteObjection, editObjection } from "../../../api/objectionAPI";
import { toast } from "react-toastify";
import { usePoster } from "../../../hooks/usePoster";
import logger from "../../../utils/logger";
import { formatDisplayDate } from "../../../utils/Date";
import Pagination from "../../../utils/Pagination";

// interface Advocate {
//   firstName: string;
//   lastName: string;
// }

interface Objection {
  _id: string;
  cnr: string;
  caseTitle: string;
  objectionType: "procedural" | "evidence" | "jurisdiction" | "legal" | "other";
  objectionTitle: string;
  description: string;
  legalGround: string;
  status: "pending" | "approved" | "rejected";
  filedDate: string;
  nextHearingDate?: string;
  priority: "low" | "medium" | "high" | "urgent";
  attachments: string[];
  // advocate: Advocate;
  courtName: string;
  firstName: string;
  lastName: string;
}

const ObjectionsPanel: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedDate] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [sortBy] = useState<"createdAt" | "priority" | "status">("createdAt");
  const [sortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const post = usePoster();
  const [objectionData, setObjectionData] = useState<{ data: Objection[] }>({
    data: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // const fetchObjections = useCallback(async () => {
  //   try {
  //     setIsLoading(true);
  //     const res = await post("/api/advocate/my-objections", {
  //       date: selectedDate || "",
  //     });
  //     logger.log(res?.data, "check objection management data");

  //     if (res?.status && res?.data) {
  //       setObjectionData({ data: res.data });
  //     } else {
  //       logger.warn("No objections found");
  //       setObjectionData({ data: [] });
  //     }
  //   } catch (err) {
  //     logger.error("Error fetching objections:", err);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, [post, selectedDate]);

  const fetchObjections = useCallback(async () => {
    try {
      setIsLoading(true);

      const res = await post("/api/advocate/my-objections", {
        page,
        limit,
        date: selectedDate || "",
      });
      logger.log("Full Response:", res);

      if (res?.status && res?.data) {
        setObjectionData({ data: res?.data });
        setTotalPages(res?.totalPages);
      } else {
        logger.warn("No objections found");
        setObjectionData({ data: [] });
        setTotalPages(1);
      }
    } catch (err) {
      logger.error("Error fetching objections:", err);
    } finally {
      setIsLoading(false);
    }
  }, [post, selectedDate, page]);

  const getStatusStats = () => {
    const pending = objectionData?.data?.filter(
      (obj: any) => obj.status === "pending",
    ).length;
    const approved = objectionData?.data?.filter(
      (obj: any) => obj.status === "approved",
    ).length;
    const rejected = objectionData?.data?.filter(
      (obj: any) => obj.status === "rejected",
    ).length;
    return { pending, approved, rejected, total: objectionData?.data?.length };
  };

  const filteredAndSortedObjections = objectionData?.data
    ?.filter((objection: Objection) => {
      const matchesSearch =
        objection?.cnr?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        objection?.caseTitle
          ?.toLowerCase()
          ?.includes(searchTerm?.toLowerCase()) ||
        objection?.objectionTitle
          ?.toLowerCase()
          ?.includes(searchTerm?.toLowerCase()) ||
        objection?.firstName
          ?.toLowerCase()
          ?.includes(searchTerm?.toLowerCase()) ||
        objection.courtName?.toLowerCase().includes(searchTerm?.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || objection.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a: any, b: any) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortBy === "priority") {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[aValue as keyof typeof priorityOrder];
        bValue = priorityOrder[bValue as keyof typeof priorityOrder];
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const stats = getStatusStats();
  const [tab, setTab] = useState("list");
  const [selectedObjection, setSelectedObjection] = useState<Objection | null>(
    null,
  );

  // update objection=============
  const handleUpdateObjection = async (updated: any) => {
    const { advocate, __v, updatedAt, createdAt, status, ...rest } = updated;
    try {
      const res = await editObjection(rest);
      let resData = res?.data;
      if (resData?.status) {
        toast.success(resData?.message || "Objection updated successfully");
        fetchObjections();
        setTab("list");
      } else {
        toast.error(resData?.message || "Failed to update objection");
      }
    } catch (error: any) {
      toast.error(error?.message);
      logger.log("error", error);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (deleteId) {
        let res = await deleteObjection(deleteId);
        let data = res?.data;
        if (data?.status) {
          toast.success(data?.message || "Objection deleted successfully");
          setShowDeleteModal(false);
          setDeleteId(null);
          fetchObjections();
        } else {
          toast.error(data?.message || "Failed to deleted Objection");
        }
      }
    } catch (error: any) {
      toast.error(error?.message);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchObjections();
  }, [fetchObjections, tab]);

  return tab === "form" ? (
    <AdvocateObjectForm
      setTab={(newTab: string) => {
        setTab(newTab);
        if (newTab === "list") {
          setSelectedObjection(null);
        }
      }}
      selectedObjection={selectedObjection}
      onSubmitEdit={handleUpdateObjection}
    />
  ) : (
    <div className=" max-w-screen w-full ">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-0">
              Objections Management
            </h1>
          </div>
          {user?.role !== "SuperAdmin" && (
            <button
              onClick={() => {
                setSelectedObjection(null);
                setTab("form");
              }}
              className="md:px-6 px-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
            >
              <Plus className="h-5 w-5" />
              New Objection
            </button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            {
              label: "Total Objections",
              value: stats.total,
              color: "blue",
              icon: FileText,
            },
            {
              label: "Pending Review",
              value: stats.pending,
              color: "yellow",
              icon: AlertCircle,
            },
            {
              label: "Approved",
              value: stats.approved,
              color: "emerald",
              icon: Check,
            },
            { label: "Rejected", value: stats.rejected, color: "red", icon: X },
          ].map((card, idx) => {
            const Icon = card.icon;
            return (
              <div
                key={idx}
                onClick={() => {
                  const filter =
                    card.label === "Total Objections"
                      ? "all"
                      : card.label === "Pending Review"
                        ? "pending"
                        : card.label === "Approved"
                          ? "approved"
                          : "rejected";

                  setStatusFilter(filter);
                  setPage(1);
                }}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition p-4 border-l-4 border-${card.color}-500 cursor-pointer ${
                  statusFilter ===
                  (card.label === "Total Objections"
                    ? "all"
                    : card.label === "Pending Review"
                      ? "pending"
                      : card.label === "Approved"
                        ? "approved"
                        : "rejected")
                    ? `ring-2 ring-${card.color}-500`
                    : ""
                }`}
              >
                <div className="sm:flex items-center justify-between">
                  <div>
                    <div
                      className={`sm:text-3xl text-lg font-bold text-${card.color}-700 mb-1`}
                    >
                      {card.value}
                    </div>
                    <div className="sm:text-sm text-xs font-medium text-gray-600">
                      {card.label}
                    </div>
                  </div>
                  <div
                    className={`sm:p-3 p-1 sm:mt-0 mt-4 bg-${card.color}-50 rounded-lg`}
                  >
                    <Icon className={`h-6 w-6 text-${card.color}-600`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="bg-white rounded-xl shadow-sm hover:shadow-md transition
 p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by CNR, title, advocate, or court..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-600 border-t pt-4 border-gray-100">
            <Filter className="h-4 w-4" />
            <span className="font-medium">
              Showing {filteredAndSortedObjections.length} of{" "}
              {objectionData.data.length} objections
            </span>
          </div>
        </div>

        {isLoading && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading objections...</p>
          </div>
        )}

        {/* Objections List */}
        {!isLoading && (
          <div className="overflow-x-auto bg-white rounded-xl shadow-sm">
            <table className="w-full">
              <thead className="bg-gray-100 border border-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    CNR Number
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Status
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Priority Level
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Hearing Date
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Filed Date
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    User Name
                  </th>
                  {user?.role !== "SuperAdmin" && (
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Action
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {filteredAndSortedObjections?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={user?.role !== "SuperAdmin" ? 7 : 6}
                      className="p-12 text-center text-gray-600"
                    >
                      <div className="inline-flex items-center justify-center l mx-auto mb-4">
                        <FileText className="h-12 w-12 text-gray-300" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-600 mb-0">
                        No Objections Found
                      </h3>
                      <p className="text-xs text-gray-400">
                        {objectionData?.data?.length === 0
                          ? "No objections have been filed yet."
                          : "No objections match your current search and filter criteria."}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedObjections.map(
                    (objection: any, index: number) => (
                      <tr
                        key={objection?._id || index}
                        className={`border border-gray-100 transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-blue-50"
                        } hover:bg-gray-50`}
                      >
                        <td className="p-2 text-gray-700 font-normal">
                          {objection.cnr || "N/A"}
                        </td>

                        <td className="p-2">
                          <span
                            className={`px-2 py-1 text-sm font-medium uppercase ${
                              objection?.status === "approved"
                                ? "text-emerald-600"
                                : objection?.status === "rejected"
                                  ? "text-rose-600"
                                  : "text-amber-600"
                            }`}
                          >
                            {objection?.status || "N/A"}
                          </span>
                        </td>

                        <td className="p-2 text-gray-700 font-normal capitalize">
                          {objection.priorityLevel || "N/A"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          {formatDisplayDate(objection.nextHearingDate) ||
                            "N/A"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          {formatDisplayDate(objection.createdAt) || "N/A"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal uppercase">
                          {objection?.advocate?.username ||
                            objection?.advocateUsername ||
                            "N/A"}
                        </td>

                        {user?.role !== "SuperAdmin" && (
                          <td className="p-2">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => {
                                  setSelectedObjection(objection);
                                  setTab("form");
                                }}
                                disabled={["approved", "rejected"].includes(
                                  objection?.status,
                                )}
                                className={`p-2 rounded-lg transition-colors ${
                                  ["approved", "rejected"].includes(
                                    objection?.status,
                                  )
                                    ? "text-gray-300 bg-gray-100 cursor-not-allowed"
                                    : "text-blue-500 hover:text-gray-800 hover:bg-white/10"
                                }`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>

                              <button
                                onClick={() => {
                                  setShowDeleteModal(true);
                                  setDeleteId(objection?._id);
                                }}
                                disabled={["approved", "rejected"].includes(
                                  objection?.status,
                                )}
                                className={`p-2 rounded-lg transition-colors ${
                                  ["approved", "rejected"].includes(
                                    objection?.status,
                                  )
                                    ? "text-gray-300 bg-gray-100 cursor-not-allowed"
                                    : "text-red-500 hover:text-red-400 hover:bg-white/10"
                                }`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ),
                  )
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
              <div className="mt-6 flex justify-end">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={(newPage: number) => setPage(newPage)}
                />
              </div>
            )}
          </div>
        )}

        {/* Enhanced Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md transform transition-all">
              <div className="flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mx-auto mb-6">
                <AlertCircle className="h-8 w-8 text-rose-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">
                Delete Objection?
              </h3>
              <p className="text-gray-600 mb-8 text-center leading-relaxed">
                Are you sure you want to delete this objection? This action
                cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all duration-200"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-6 py-3 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-70"
                  disabled={isDeleting}
                  onClick={() => handleDelete()}
                >
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectionsPanel;
